<script lang="ts">
  import { Alert, Icon, Title, Button, ConfirmActions } from '$shared/components';
  import { middleEllipsis } from '$shared/helpers/utils';
  import { AlertOption } from '$shared/models/AlertOption';
  import type { AlertDetailedInfo } from '$shared/models/AlertDetailedInfo';
  import { IconType } from '$shared/constants/IconType';
  import { onMount } from 'svelte';

  interface Props {
    folderName?: string;
    error?: AlertDetailedInfo | string;
    values?: any[];
    onsave?: (files: File[]) => void;
    onchange?: (files: File[]) => void;
    oncloseerror?: () => void;
    oncancel?: () => void;
  }

  let {
    folderName = '',
    error = '',
    values = [],
    onsave,
    onchange,
    oncloseerror,
    oncancel
  }: Props = $props();

  let fileInput: HTMLElement = $state();
  let fileForm: HTMLFormElement = $state();
  let files: File[] = $state([]);
  let isdragOver = $state(false);

  onMount(() => {
    if (Array.isArray(values) && values.length !== 0) {
      files = [...values];
    }
  });

  const onClickSave = (): void => {
    onsave?.(files);
  };

  const onClickChange = (): void => {
    onchange?.(files);
  };

  const onClickUpload = () => {
    if (fileInput) {
      fileInput.click();
    }
  };

  const onFileInputChange = (e: any) => {
    if (e.target.files.length === 0) {
      return;
    }
    files = files.concat(Array.from(e.target.files));
    onClickChange();
    fileForm?.reset();
  };

  const removeFile = (index: number) => {
    if (index < 0 || !files[index]) return;
    files.splice(index, 1);
    files = [...files];
    onClickChange();
  };

  const onClickCloseError = (): void => {
    oncloseerror?.();
  };

  const onDragOver = () => {
    isdragOver = true;
  };

  const onDragLeave = () => {
    isdragOver = false;
  };

  const onDrop = (e: any): void => {
    isdragOver = false;
    const data = e.dataTransfer.items;
    const tempFiles = [];
    for (let i = 0; i < data.length; i++) {
      tempFiles.push(data[i].getAsFile());
    }
    files = files.concat(tempFiles);
    onClickChange();
  };
  let disabled = $derived(!folderName || files.length === 0);
</script>

<div
  class="add-files"
  class:isdragOver
  ondrop={(e) => {
    e.preventDefault();
    onDrop(e);
  }}
  ondragover={(e) => {
    e.preventDefault();
    onDragOver();
  }}
  ondragleave={(e) => {
    e.preventDefault();
    onDragLeave();
  }}
>
  <div class="title">
    <Title name="folderNameTitle" type="h2">Add files to {folderName || ''}</Title>
  </div>
  {#if error}
    <div class="error">
      <Alert
        maxWidth
        closable
        data={error}
        options={AlertOption.errorAlert()}
        onclose={onClickCloseError}
      />
    </div>
  {/if}
  <div class="input">
    <div class="label">Drop file here or</div>
    <div class="upload-btn">
      <Button icon="icon-upload" name="upload" onclick={onClickUpload}>Upload</Button>
    </div>
  </div>
  <div class="files" class:hide={files.length === 0}>
    {#each files as file, index}
      <div class="file" data-cy={file.name}>
        <div class="name" title={file.name}>
          <div class="icon"><Icon value="icon-file" /></div>
          <div class="text">{middleEllipsis(file.name, 30)}</div>
        </div>
        <div class="file-action">
          <Icon
            name="remove-icon"
            type={IconType.Button}
            value="icon-trash"
            onclick={() => {
              removeFile(index);
            }}
          />
        </div>
      </div>
    {/each}
  </div>
  <div class="actions">
    <ConfirmActions
      {disabled}
      reverse
      okLabel="Save"
      cancelLabel="Back"
      onok={onClickSave}
      {oncancel}
    />
  </div>

  <form
    onsubmit={(e) => {
      e.preventDefault();
    }}
    bind:this={fileForm}
  >
    <input
      data-cy="fileInput"
      bind:this={fileInput}
      type="file"
      multiple
      hidden
      onchange={onFileInputChange}
    />
  </form>
</div>

<style>
  .error {
    margin-top: 16px;
  }
  .add-files.isdragOver .input {
    background-color: #fff;
    border: 1px solid var(--blue);
  }
  .input {
    margin-top: 16px;
    height: 75px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--grey-bg);
    border-radius: 10px;
    font-size: 14px;
    color: var(--grey-medium);
  }
  .input .label {
    margin-right: 16px;
  }
  .actions {
    margin-top: 24px;
  }
  .files {
    margin-top: 16px;
    border: 1px solid #eee;
    border-radius: 10px;
    max-height: calc(70vh - 225px);
    overflow: auto;
  }
  .files.hide {
    display: none;
  }
  .files .file {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 50px;
    padding: 0 16px;
    border-bottom: 1px solid #eee;
  }
  .files .file .name {
    display: flex;
  }
  .files .file .name .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
  }
</style>
